import React, { useState, useEffect } from "react";
import AdvanceTableWrapper from "../advance-table/AdvanceTableWrapper";
import AdvanceTable from "../advance-table/AdvanceTable";
import AdvanceTableFooter from "../advance-table/AdvanceTableFooter";
import usePagination from "../advance-table/usePagination";
import { Button, Form, InputGroup, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { ApiConfig } from "config/ApiConfig";
import axios from "axios";
import { getApiConfig } from "helpers/utils";
import { ShowToastError } from "components/Widgets/ShowToastError";

export default function GroupSelection({
  app_module,
  remaining_groups,
  allowed_groups,
  survey_name,
  closeModal,
  updateGroups,
  setCount,
}) {
  const [combinedGroups, setCombinedGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroups, setSelectedGroups] = useState(allowed_groups || []);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [existingTemplates, setExistingTemplates] = useState({});

  // Fetch existing templates on component mount
  useEffect(() => {
    const fetchFeatureData = async () => {
      const config = getApiConfig();
      try {
        const response = await axios.get(ApiConfig.xSurveyDetails, config);
        setExistingTemplates(response?.data?.settings || {});
      } catch (error) {
        ShowToastError({ error });
      }
    };

    fetchFeatureData();
  }, []);

  // Combine and sort groups so that allowed_groups come first
  useEffect(() => {
    const combined = [...remaining_groups, ...allowed_groups];
    const sortedCombined = combined.sort((a, b) => {
      const isAAllowed = allowed_groups.some((group) => group.id === a.id);
      const isBAllowed = allowed_groups.some((group) => group.id === b.id);

      if (isAAllowed && !isBAllowed) return -1; // a comes first
      if (!isAAllowed && isBAllowed) return 1; // b comes first
      return 0; // no change in order
    });

    setCombinedGroups(sortedCombined);
    setFilteredGroups(sortedCombined);
  }, [remaining_groups, allowed_groups]);

  // Filter groups based on search term
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredGroups(combinedGroups);
    } else {
      setFilteredGroups(
        combinedGroups.filter((group) =>
          group.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, combinedGroups]);

  // Handle checkbox change (assign/unassign)
  const handleCheckChange = (is_checked, selectedGroup) => {
    setSelectedGroups((prevSelectedGroups) => {
      if (is_checked) {
        return [...prevSelectedGroups, selectedGroup];
      } else {
        return prevSelectedGroups.filter(
          (group) => group.id !== selectedGroup.id
        );
      }
    });
  };

  // Handle updating groups via API
  const handleUpdateGroups = async () => {
    const newTemplateKey = survey_name?.key.toLowerCase().replace(/ /g, "_");
    if (!existingTemplates[newTemplateKey]) {
      toast.error("Template not found. Please select a valid survey.");
      return;
    }

    // Create the updated template with only the currently selected groups
    const updatedTemplate = {
      ...existingTemplates[newTemplateKey],
      selected_groups: selectedGroups.map((group) => ({
        name: group.name,
        description: group.description,
        ticket_count: group.ticket_count,
        id: group.id,
      })),
    };

    // Merge the updated template with existing templates
    const updatedTemplates = {
      ...existingTemplates,
      [newTemplateKey]: updatedTemplate,
    };

    try {
      setIsLoadingGroups(true);
      const config = getApiConfig();

      // Send the updated templates to the API
      await axios.post(
        ApiConfig.xSurveyUpdateSettings,
        { settings: updatedTemplates },
        config
      );

      // Show success message
      toast.success("Groups updated successfully in the template");

      // Update the state of remainingGroups and ingestedGroups
      const updatedRemainingGroups = remaining_groups.filter(
        (group) => !selectedGroups.some((selected) => selected.id === group.id)
      );
      const updatedIngestedGroups = selectedGroups;

      // Pass the updated groups back to the parent component
      updateGroups(updatedRemainingGroups, updatedIngestedGroups);

      // Reset the selectedGroups state to empty
      setSelectedGroups([]);
    } catch (error) {
      ShowToastError({ error });
    } finally {
      setIsLoadingGroups(false);
      closeModal(false);
      setCount((prevCount) => prevCount + 1);
    }
  };

  // Table columns configuration
  const groupsTableColumns = [
    {
      accessor: "name",
      Header: "Group Name",
      Cell: ({ row }) => {
        const isAllowedGroup = allowed_groups.some(
          (group) => group.id === row?.original?.id
        );
        return (
          <Form.Check
            type="checkbox"
            name="groupSelection"
            id={`checkbox-${row?.original?.name}`}
            className="my-auto"
            label={<p className="m-0 ms-2">{row?.original?.name}</p>}
            checked={selectedGroups.some(
              (group) => group.id === row?.original?.id
            )}
            onChange={(e) => handleCheckChange(e.target.checked, row?.original)}
          />
        );
      },
    },
    {
      accessor: "description",
      Header: "Description",
      Cell: ({ row }) => (
        <p className="m-0 text-wrap">{row.original.description}</p>
      ),
    },
  ];

  // Pagination logic
  const {
    currentPageData: currentPageGroups,
    setPageSize: setPageSizeGroups,
    nextPage: nextPageGroups,
    previousPage: previousPageGroups,
    canNextPage: canNextPageGroups,
    canPreviousPage: canPreviousPageGroups,
    resetPagination: resetPaginationGroups,
    pageIndex: pageIndexGroups,
    pageSize: pageSizeGroups,
  } = usePagination(filteredGroups);

  return (
    <div className="my-2">
      <h6 className="mb-2">Groups</h6>
      <Row className="mb-3">
        <Col xs={12} sm={6} md={6} lg={6}>
          <InputGroup size="sm">
            <Form.Control
              placeholder="Search groups..."
              aria-label="Search Groups"
              aria-describedby="basic-addon2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <InputGroup.Text id="basic-addon2">
              <FontAwesomeIcon icon="search" />
            </InputGroup.Text>
          </InputGroup>
        </Col>
      </Row>
      <AdvanceTableWrapper
        key={pageSizeGroups}
        columns={groupsTableColumns}
        data={currentPageGroups}
        sortable
        pagination
        perPage={pageSizeGroups}
      >
        <AdvanceTable
          table
          headerClassName="bg-200 text-900 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs--1 mb-0 overflow-hidden",
          }}
        />
        {filteredGroups.length ? (
          <>
            <AdvanceTableFooter
              pageIndex={pageIndexGroups}
              pageSize={pageSizeGroups}
              nextPage={nextPageGroups}
              previousPage={previousPageGroups}
              canNextPage={canNextPageGroups}
              canPreviousPage={canPreviousPageGroups}
              page={currentPageGroups}
              rowCount={filteredGroups.length}
              setPageSize={setPageSizeGroups}
              rowInfo
              rowsPerPageSelection
              navButtons
            />
            <div
              className="btn-group mx-auto d-flex justify-content-center w-25 mt-2"
              role="group"
              aria-label="Basic mixed styles example"
            >
              <Button
                variant="falcon-success"
                disabled={isLoadingGroups}
                onClick={handleUpdateGroups}
              >
                {isLoadingGroups ? "Updating" : "Update Groups"}
              </Button>
            </div>
          </>
        ) : (
          <h5 className="text-muted text-center my-4">No Data Found</h5>
        )}
      </AdvanceTableWrapper>
    </div>
  );
}
